import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import App from './App';
import { setupInterceptors } from './interceptors/axiosInterceptors';
import { AppConfig } from '@/AppConfig';

import '@antimatterhq/ui/css/base.css';
import '@antimatterhq/ui/css/variables.css';

async function initialize() {
  setupInterceptors();
  // Fetch this from environment config in case we want to dynamically enable profiling
  try {
    const response = await fetch('/environment');
    const data = (await response.json()) as AppConfig;

    if (data.MSW_ENABLE_MOCKING) {
      const { worker } = await import('../mocks/msw/browser');

      await worker(data.MSW_ENABLE_MOCKING).start({
        onUnhandledRequest: () => {},
      });
    }

    if (data.DATADOG_APPLICATION_ID && data.DATADOG_CLIENT_TOKEN) {
      // initialize datadog logs regardless of environment
      datadogLogs.init({
        clientToken: data.DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'asl-web-client',
        env: data.DATADOG_ENV,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: undefined,
        beforeSend: (event) => {
          // xhr requests are handling manually by axios interceptor
          if (event.message.includes('XHR')) {
            return false;
          }

          // ignore axios errors that don't have a request or response, these are duplicates
          if (
            event.error?.kind === 'AxiosError' &&
            !event.request &&
            !event.response
          ) {
            return false;
          }

          return true;
        },
      });

      // Don't initialize datadog rum for local development
      if (data.NODE_ENV === 'local') {
        return data;
      }

      datadogRum.init({
        applicationId: data.DATADOG_APPLICATION_ID,
        clientToken: data.DATADOG_CLIENT_TOKEN,
        version: import.meta.env.VITE_VERSION,
        site: 'datadoghq.com',
        service: 'antimatter-web',
        env: data.DATADOG_ENV,
        defaultPrivacyLevel: 'mask-user-input',
        traceSampleRate: 100,
        sessionSampleRate: 100,
        telemetrySampleRate: 100,
        sessionReplaySampleRate: 0,
      });
    }

    return data;
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

async function render() {
  const config = await initialize();

  const root = createRoot(document.getElementById('root')!);

  root.render(
    <StrictMode>
      <App config={config} />
    </StrictMode>,
  );
}

render();
